import { z } from "zod";

/**
 * Shared DTO — Admin notification-template registry (US-AE7.F5-0025 · @UC9064).
 *  - GET /admin/notification-templates — xem registry file-based theo event_type × locale × channel,
 *    soát "thiếu key" bản dịch đang dùng (tiền đề AC3; CI i18n gate đầy đủ = AE10).
 *
 * MỘT NGUỒN SỰ THẬT shape cho api (S01) + admin web (S03) — tránh drift (DRY).
 *
 * BẤT BIẾN (invariants):
 *  - no-PII (C2): CHỈ metadata (event_type, locale, channel, present/missingKeys) — KHÔNG text render/PII.
 *  - no-SMS (C1): channel enum = in_app | push | email (khớp TEMPLATE_CHANNELS domain).
 *  - Bundle ['vi','en'] (locale-policy). `complete` = mọi locale present & không missingKeys.
 *  - `.strict()` — reject field lạ tại biên.
 *  - READ-only (template sửa qua CI/PR — không CRUD runtime).
 *
 * Envelope trả CẢ HAI hình:
 *  - `items`: ma trận event_type × locale (soát thiếu key) — cho DTO/kiểm S02.
 *  - `rows` : hàng phẳng event×locale×channel — KHỚP cột resources.ts (event/locale/channel/ver/status/
 *    updated) để ResourceListPage engine render KHÔNG đổi UI (SD-10 bảo toàn UI).
 */

/** Kênh template (no-SMS · C1) — khớp domain TEMPLATE_CHANNELS. */
export const notifTemplateChannelSchema = z.enum(["in_app", "push", "email"]);
export type NotifTemplateChannel = z.infer<typeof notifTemplateChannelSchema>;

/** Soát 1 locale của 1 event_type — present + missingKeys (metadata, KHÔNG text). */
export const notifTemplateLocaleAuditSchema = z
  .object({
    locale: z.string(),
    present: z.boolean(),
    missingKeys: z.array(z.string()),
  })
  .strict();
export type NotifTemplateLocaleAudit = z.infer<typeof notifTemplateLocaleAuditSchema>;

/** Ma trận 1 event_type × locale + cờ complete. */
export const notifTemplateRowSchema = z
  .object({
    eventType: z.string(),
    locales: z.array(notifTemplateLocaleAuditSchema),
    complete: z.boolean(),
  })
  .strict();
export type NotifTemplateRow = z.infer<typeof notifTemplateRowSchema>;

/**
 * Hàng phẳng cho ResourceListPage engine (KHỚP resources.ts `templates` columns).
 *  - status = enabled | incomplete (enabled nếu locale present & đủ key; ngược lại incomplete).
 *  - ver = phiên bản registry (v1 — registry file, chưa versioning per-entry).
 */
export const notifTemplateEngineRowSchema = z
  .object({
    id: z.string(),
    event: z.string(),
    locale: z.string(),
    channel: notifTemplateChannelSchema,
    ver: z.string(),
    status: z.enum(["enabled", "incomplete"]),
    updated: z.string(),
    // US-AE7.F5-0153 (@UC9153) — cờ "Đã tuỳ chỉnh": entry có override enabled trong DB (lớp phủ trên
    // registry). optional (default false) để backward-compat client cũ (engine hàng phẳng).
    overridden: z.boolean().optional(),
  })
  .strict();
export type NotifTemplateEngineRow = z.infer<typeof notifTemplateEngineRowSchema>;

/** Envelope GET /admin/notification-templates. */
export const notifTemplatesResponseSchema = z
  .object({
    items: z.array(notifTemplateRowSchema),
    rows: z.array(notifTemplateEngineRowSchema),
    supportedLocales: z.array(z.string()),
    defaultLocale: z.string(),
  })
  .strict();
export type NotifTemplatesResponse = z.infer<typeof notifTemplatesResponseSchema>;

/**
 * Derive `complete` từ locales (dùng client-side khi cần re-check; server đã set). Metadata thuần.
 */
export function isTemplateComplete(locales: readonly NotifTemplateLocaleAudit[]): boolean {
  return locales.length > 0 && locales.every((l) => l.present && l.missingKeys.length === 0);
}

/**
 * ── Preview render + validate (US-AE-IMP.F5-0117-S01 · @UC9147) ─────────────────────────────────
 * Option B (PO chốt 06/07): READ-ONLY preview 1 template render THẬT với params MẪU + trạng thái
 * validate. KHÔNG mutate registry (CI-owned). KHÔNG CRUD runtime.
 *
 * Khác list (metadata thuần): preview CÓ title/body render — nhưng bằng params MẪU cố định, KHÔNG
 * dữ liệu người dùng thật (no-PII C2). money = BigInt minor units, ICU chỉ format hiển thị (B1).
 */

/**
 * Query GET /admin/notification-templates/:eventType/preview.
 *  - `locale` PERMISSIVE (chuỗi bất kỳ) — render engine tự fallback về defaultLocale nếu ngoài bundle
 *    (AC1b, không 400). `channel` = enum no-SMS (ngoài enum → 400 tại biên).
 */
export const notifTemplatePreviewQuerySchema = z
  .object({
    locale: z.string().min(1).optional(),
    channel: notifTemplateChannelSchema.optional(),
  })
  .strict();
export type NotifTemplatePreviewQuery = z.infer<typeof notifTemplatePreviewQuerySchema>;

/** Mã lỗi render — đồng bộ domain `TemplateRenderError.code` (một nguồn sự thật phân loại lỗi). */
export const notifTemplateRenderErrorCodeSchema = z.enum([
  "TEMPLATE_NOT_FOUND",
  "LOCALE_NOT_AVAILABLE",
  "CHANNEL_NOT_AVAILABLE",
  "MISSING_PARAM_KEY",
  "INVALID_MONEY_PARAM",
]);
export type NotifTemplateRenderErrorCode = z.infer<typeof notifTemplateRenderErrorCodeSchema>;

/**
 * Response preview 1 template (event × locale × channel) — render THẬT + validation status.
 *  - `valid:true` ⇒ có `title`/`body` render (params mẫu). `valid:false` ⇒ có `errorCode` (KHÔNG 500 —
 *    lỗi template là DỮ LIỆU hợp lệ để hiển thị), title/body vắng.
 *  - `locale` = locale THỰC dùng render (sau fallback). `missingKeys` = required key thiếu (auditTemplate).
 *  - `complete` = template đủ mọi locale-channel & không thiếu key.
 */
export const notifTemplatePreviewResponseSchema = z
  .object({
    eventType: z.string(),
    locale: z.string(),
    channel: notifTemplateChannelSchema,
    title: z.string().optional(),
    body: z.string().optional(),
    valid: z.boolean(),
    errorCode: notifTemplateRenderErrorCodeSchema.optional(),
    missingKeys: z.array(z.string()),
    complete: z.boolean(),
    // US-AE7.F5-0153 (@UC9153) — preview đang dùng override DB (title/body render từ override) hay
    // registry file. optional (backward-compat). Preview honor override cùng resolver worker (1 nguồn).
    overridden: z.boolean().optional(),
  })
  .strict();
export type NotifTemplatePreviewResponse = z.infer<typeof notifTemplatePreviewResponseSchema>;

/**
 * Body POST /admin/notification-templates/:eventType/report-issue (AC3, no-deceive).
 *  - `note` = ghi chú operator (tuỳ chọn, giới hạn 1000 ký tự). `locale`/`channel` = bối cảnh lỗi.
 */
export const notifTemplateReportIssueBodySchema = z
  .object({
    note: z.string().trim().max(1000).optional(),
    locale: z.string().min(1).optional(),
    channel: notifTemplateChannelSchema.optional(),
  })
  .strict();
export type NotifTemplateReportIssueBody = z.infer<typeof notifTemplateReportIssueBodySchema>;

/**
 * Response report-issue — `recorded:true` CHỈ khi THẬT ghi vết audit (no-deceive). Không có sink thật
 * ⇒ endpoint không tồn tại / UI empty-state (KHÔNG nút giả).
 */
export const notifTemplateReportIssueResponseSchema = z
  .object({
    recorded: z.literal(true),
    eventType: z.string(),
  })
  .strict();
export type NotifTemplateReportIssueResponse = z.infer<typeof notifTemplateReportIssueResponseSchema>;

/**
 * ── Runtime override CRUD (US-AE7.F5-0153-S02 · @UC9153) ─────────────────────────────────────────
 * PO chốt 09/07: admin sửa mẫu tại runtime qua DB override (lớp phủ trên registry file). Registry file
 * = default; override = lớp phủ. Render (preview + gửi thật) ưu tiên override enabled → fallback registry.
 *
 * BẤT BIẾN:
 *  - no-SMS (C1): channel ∈ in_app|push|email (khớp domain).
 *  - Bundle ['vi','en'] (locale-policy): locale STRICT enum (KHÁC preview permissive — override phải khớp
 *    1 tổ hợp cụ thể, không fallback). locale/channel lạ → 400 tại biên.
 *  - title/body bắt buộc (không rỗng). Validate ICU + requiredKeys STRICT ở tầng service (422 — USER CHỐT).
 *  - `.strict()` reject field lạ.
 */

/** Locale override STRICT (không permissive như preview) — chỉ bundle vi/en. */
export const notifTemplateOverrideLocaleSchema = z.enum(["vi", "en"]);
export type NotifTemplateOverrideLocale = z.infer<typeof notifTemplateOverrideLocaleSchema>;

/**
 * Body PUT /admin/notification-templates/:eventType — upsert override cho (locale,channel).
 *  - title/body ICU-subset (validate placeholder + requiredKeys STRICT ở service → 422 nếu sai).
 *  - enabled optional (default true) — false = tắt override, render quay về registry (giữ bản để bật lại).
 */
export const notifTemplateOverrideBodySchema = z
  .object({
    locale: notifTemplateOverrideLocaleSchema,
    channel: notifTemplateChannelSchema,
    title: z.string().trim().min(1).max(500),
    body: z.string().trim().min(1).max(2000),
    enabled: z.boolean().optional(),
  })
  .strict();
export type NotifTemplateOverrideBody = z.infer<typeof notifTemplateOverrideBodySchema>;

/** Query DELETE /admin/notification-templates/:eventType/override — reset 1 tổ hợp về registry. */
export const notifTemplateDeleteOverrideQuerySchema = z
  .object({
    locale: notifTemplateOverrideLocaleSchema,
    channel: notifTemplateChannelSchema,
  })
  .strict();
export type NotifTemplateDeleteOverrideQuery = z.infer<typeof notifTemplateDeleteOverrideQuerySchema>;

/**
 * Response upsert/delete override — entry chuẩn hoá + `overridden` (true sau upsert enabled; false sau
 * delete / disable). Preview refetch phản ánh nội dung mới (title/body render lại từ resolver).
 */
export const notifTemplateOverrideResponseSchema = z
  .object({
    eventType: z.string(),
    locale: notifTemplateOverrideLocaleSchema,
    channel: notifTemplateChannelSchema,
    overridden: z.boolean(),
  })
  .strict();
export type NotifTemplateOverrideResponse = z.infer<typeof notifTemplateOverrideResponseSchema>;
